/* ============================================================
   Dathent — Post Preview screen
   Deep view from Library / Calendar. Phone-frame mock, swipeable
   carousel, fullscreen lightbox, right-side details panel with
   status-conditional actions. Source: design pack pages/post-preview.jsx.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons, BrandTints } from '../components/pack-icons/brands';
import { useActivity } from '../components/AppShell';

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  if (v.includes('you')) return 'youtube';
  return v;
};

function fmt(n) {
  if (n == null) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k';
  return String(n);
}
function fmtDate(d) {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function cleanPromptPrefix(s) {
  if (typeof s !== 'string') return s;
  return s.replace(/^Generating(?:\s+\d+\s*\/\s*\d+)?\s*:\s*/i, '').trim();
}

/* Pick the most representative target for display: prefer a successfully published
   one, then any scheduled one, then whatever is first. */
function pickRepresentativeTarget(targets, opts = {}) {
  if (!targets?.length) return null;
  // Caller can specify which target/platform they navigated FROM (e.g. user
  // clicked the Telegram card in Library — they want the Telegram chrome).
  if (opts.targetId) {
    const exact = targets.find((t) => t?.id === opts.targetId);
    if (exact) return exact;
  }
  if (opts.platform) {
    const wantPlatform = String(opts.platform).toLowerCase();
    const byPlatform = targets.find((t) => String(t?.platform || '').toLowerCase() === wantPlatform);
    if (byPlatform) return byPlatform;
  }
  return (
    targets.find((t) => t?.status === 'published' && t?.publishedAt)
    || targets.find((t) => t?.status === 'scheduled' || t?.status === 'queued')
    || targets[0]
  );
}

/* Old Threads posts were stored with two kinds of broken permalinks:
     a) placeholder "@-" handle: https://www.threads.net/@-/post/<id>
     b) numeric Meta ID instead of the public shortcode:
        https://www.threads.com/@<handle>/post/17930976774268969
        — Threads' public router only resolves SHORTCODES (DYlj8XgFECZ-style),
          not the API's internal numeric ID, so these URLs hit the "Not all
          who wander are lost…" 404.
   New posts get the proper permalink from the worker (post-publish
   permalink fetch). For broken legacy rows the frontend can't synthesize
   a shortcode out of thin air, so we degrade gracefully:
     - Good shortcode URL → return as-is.
     - Numeric-ID or @-/-placeholder URL → fall back to the author's
       profile (which always works) — better than a broken /post link.
     - No URL and no handle → return null so the UI hides the "View" CTA.
   The backend backfill task is the canonical fix for legacy rows once
   Meta returns a permalink for the post. */
function rewriteThreadsPermalink(url, platform, externalPostId, handle) {
  if (platform === 'threads') {
    return rewriteMetaPermalink(url, handle, {
      slugSegment: '/post/',
      profileBase: 'https://www.threads.com/@',
      placeholderHandleRegex: /\/@-\/post\//,
    });
  }
  if (platform === 'instagram') {
    return rewriteMetaPermalink(url, handle, {
      slugSegment: '/p/',
      profileBase: 'https://www.instagram.com/',
      placeholderHandleRegex: null,
    });
  }
  return url;
}

/* Shared logic for Threads + Instagram permalink degradation. Meta's public
   routers (threads.com and instagram.com) only resolve SHORTCODE URLs, not the
   numeric internal IDs returned by the Graph API at publish time. Until the
   backend backfill restores the proper shortcode, fall back to the author's
   profile URL — always works, never 404s. */
function rewriteMetaPermalink(url, handle, opts) {
  const { slugSegment, profileBase, placeholderHandleRegex } = opts;
  const cleanHandle = handle ? String(handle).replace(/^@/, '').trim() : '';
  if (typeof url !== 'string' || !url) {
    return cleanHandle ? `${profileBase}${encodeURIComponent(cleanHandle)}` : null;
  }
  // URL exists. Inspect the post slug: real Meta shortcodes have at least one
  // letter (e.g. DYlj8XgFECZ); the broken-numeric form is digits-only.
  const slugMatch = url.match(new RegExp(`${slugSegment.replace(/\//g, '\\/')}([^/?#]+)`));
  const slug = slugMatch?.[1] || '';
  const slugIsShortcode = slug && /[A-Za-z]/.test(slug);
  const hasPlaceholderHandle = placeholderHandleRegex ? placeholderHandleRegex.test(url) : false;
  if (slugIsShortcode && !hasPlaceholderHandle) {
    return url; // Good URL from Meta — leave as-is.
  }
  // Numeric/placeholder URL — degrade to profile.
  if (cleanHandle) {
    return `${profileBase}${encodeURIComponent(cleanHandle)}`;
  }
  return null;
}

/* Map a server content row into the shape our chromes expect. */
function mapServerPost(raw, targetHint = {}) {
  if (!raw) return null;
  const item = raw.contentItem || raw;
  const v = raw.activeVersion;
  const targets = raw.publishSummary?.targets || [];
  const repTarget = pickRepresentativeTarget(targets, targetHint);
  // Chrome priority: matching publish_target → URL hint → published →
  // scheduled → batch.platforms[0] (only present for in-flight or drafts) →
  // 'linkedin' as last resort.
  const batchPlatform = Array.isArray(raw?.batch?.platforms) ? raw.batch.platforms[0] : null;
  const platform = PLATFORM_FROM_NAME(
    repTarget?.platform
    || targetHint.platform
    || raw.publishSummary?.publishedPlatforms?.[0]
    || raw.publishSummary?.platforms?.[0]
    || batchPlatform
    || item.platform
  ) || targetHint.platform || batchPlatform || 'linkedin';
  const tint = BrandTints[platform] || BrandTints.x;
  const fallbackGrad = [tint.from, tint.to];

  const slidesRaw = raw.carouselSlides || [];
  const mediaUrls = raw.mediaUrls || [];

  let slides;
  if (slidesRaw.length > 0) {
    slides = slidesRaw.map((s, i) => ({
      kind: 'image',
      cap: s.caption || '',
      grad: fallbackGrad,
      imageUrl: s.mediaAsset?.publicUrl || s.imageUrl || mediaUrls[i] || null,
      serif: false,
      tag: `${String(i + 1).padStart(2, '0')} / ${String(slidesRaw.length).padStart(2, '0')}`,
    }));
  } else if (mediaUrls.length > 0) {
    slides = mediaUrls.map((url, i) => ({
      kind: 'image',
      cap: '',
      grad: fallbackGrad,
      imageUrl: url,
      serif: false,
      tag: mediaUrls.length > 1
        ? `${String(i + 1).padStart(2, '0')} / ${String(mediaUrls.length).padStart(2, '0')}`
        : '',
    }));
  } else {
    const heroImage = v?.imageUrl || raw.mediaPreview?.url || null;
    slides = [{
      kind: 'image',
      cap: '',
      grad: fallbackGrad,
      imageUrl: heroImage,
      serif: false,
      tag: '',
    }];
  }

  // Per-platform copy when activeVersion.platformVersions has it. Backend
  // ships per-platform variants in content_versions.platform_versions_json
  // (e.g. Threads has its own 500-char tighter version). Without this every
  // platform chrome shows the same generic body and the user thinks the
  // adaptation didn't happen.
  const pv = v?.platformVersions || {};
  const pvAliasKey = platform === 'x' ? 'twitter' : platform === 'twitter' ? 'x' : null;
  const pvDirect = pv[platform];
  const pvAliased = pvAliasKey ? pv[pvAliasKey] : null;
  const platformBodyStr = (typeof pvDirect === 'string' && pvDirect.trim())
    ? pvDirect
    : (pvDirect && typeof pvDirect === 'object' && typeof pvDirect.body === 'string' && pvDirect.body.trim())
      ? pvDirect.body
      : (typeof pvAliased === 'string' && pvAliased.trim())
        ? pvAliased
        : (pvAliased && typeof pvAliased === 'object' && typeof pvAliased.body === 'string' && pvAliased.body.trim())
          ? pvAliased.body
          : null;
  const captionRaw = platformBodyStr || v?.body || v?.caption || item.body || '';
  const hookRaw = (platformBodyStr ? platformBodyStr.split('\n')[0] : null) || v?.hook || item.title || '';
  const title = cleanPromptPrefix(hookRaw || captionRaw.split('\n')[0]) || 'Untitled post';
  const caption = cleanPromptPrefix(captionRaw) || cleanPromptPrefix(item.body || '');

  // Hashtags: try compact platform versions, fallback to extracting from body
  const hashtags = (v?.platformVersions?.[platform]?.hashtags
    || v?.hashtags
    || extractHashtags(caption));

  const metrics = raw.metrics ? {
    views: raw.metrics.views ?? 0,
    likes: raw.metrics.likes ?? 0,
    comments: raw.metrics.comments ?? 0,
    shares: raw.metrics.shares ?? 0,
    saves: raw.metrics.saves ?? 0,
    reach: raw.metrics.reach ?? raw.metrics.impressions ?? 0,
    engagement: typeof raw.metrics.engagement === 'number' ? raw.metrics.engagement : null,
  } : null;

  // Compute engagement if missing
  if (metrics && metrics.engagement == null && metrics.views > 0) {
    const interactions = metrics.likes + metrics.comments + metrics.shares + metrics.saves;
    metrics.engagement = (interactions / metrics.views) * 100;
  }

  // Real handle from the target's social account (if available).
  // Prefer display_name; fall back to platform-specific username in
  // capabilities (e.g. capabilities.threadsUsername for Threads); only
  // use the numeric external_account_id as a last resort so the chrome
  // shows "@yourhandle" instead of "@123456789".
  const accountCaps = repTarget?.socialAccount?.capabilities || {};
  const fallbackUsername = (
    accountCaps.threadsUsername
    || accountCaps.instagramUsername
    || accountCaps.facebookUsername
    || accountCaps.tiktokUsername
    || accountCaps.linkedinUsername
    || accountCaps.username
    || null
  );
  const accountDisplayName = repTarget?.socialAccount?.displayName
    || (fallbackUsername ? `@${String(fallbackUsername).replace(/^@/, '')}` : null)
    || repTarget?.socialAccount?.externalAccountId
    || '';
  const accountId = repTarget?.socialAccount?.externalAccountId || '';

  return {
    id: item.id,
    platform,
    status: item.status || 'draft',
    scheduledAt: repTarget?.scheduledAt || raw.publishSummary?.lastScheduledAt || null,
    publishedAt: repTarget?.publishedAt || raw.publishSummary?.lastPublishedAt || null,
    permalink: rewriteThreadsPermalink(
      repTarget?.permalink || repTarget?.externalUrl || null,
      platform,
      repTarget?.externalPostId,
      accountDisplayName || accountId
    ),
    externalPostId: repTarget?.externalPostId || null,
    scheduledPostId: raw.publishSummary?.scheduledPostId || null,
    targetId: repTarget?.id || null,
    handle: accountDisplayName || (accountId ? `@${accountId.replace(/^@/, '')}` : ''),
    handleSub: '',
    title,
    caption,
    hashtags,
    altText: v?.altText || '',
    metrics,
    slides,
    pillar: null,
    pillarColor: null,
    createdAt: item.createdAt,
    cost: null,
  };
}

function extractHashtags(body = '') {
  const m = String(body).match(/#[\p{L}\p{N}_]+/gu);
  return m ? Array.from(new Set(m)) : [];
}

/* ============================================================
   Shared sub-components
   ============================================================ */

function MediaCarousel({ slides, slideIdx, setSlideIdx, openLightbox, aspect = '1 / 1', rounded = false, full = false }) {
  if (!slides?.length) return null;
  const slide = slides[slideIdx] || slides[0];
  const next = () => setSlideIdx((i) => Math.min(slides.length - 1, i + 1));
  const prev = () => setSlideIdx((i) => Math.max(0, i - 1));

  const slideStyle = slide.imageUrl
    ? { backgroundImage: `url("${slide.imageUrl}")` }
    : { background: `linear-gradient(135deg, ${slide.grad[0]}, ${slide.grad[1]})` };

  return (
    <div
      className={`pp-media ${rounded ? 'is-rounded' : ''} ${full ? 'is-full' : ''}`}
      style={{ aspectRatio: aspect }}
      onClick={openLightbox}
    >
      <div className="pp-media-slide" style={slideStyle}>
        <div className="pp-media-grad" />
        {slide.cap && (
          <div
            className="pp-media-cap"
            style={slide.serif ? { fontFamily: 'var(--font-serif)', fontStyle: 'italic' } : undefined}
          >
            {slide.cap.split('\n').map((line, i) => (<div key={i}>{line}</div>))}
          </div>
        )}
        {slide.tag && <span className="pp-media-tag mono">{slide.tag}</span>}
        <span className="pp-media-zoom"><I.eye size={14} /></span>
      </div>
      {slides.length > 1 && (
        <>
          {slideIdx > 0 && (
            <button
              className="pp-media-nav pp-media-nav-l"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              type="button"
            >
              <I.chevLeft size={16} />
            </button>
          )}
          {slideIdx < slides.length - 1 && (
            <button
              className="pp-media-nav pp-media-nav-r"
              onClick={(e) => { e.stopPropagation(); next(); }}
              type="button"
            >
              <I.chevRight size={16} />
            </button>
          )}
          <div className="pp-media-dots">
            {slides.map((_, i) => (
              <span key={i} className={`pp-media-dot ${i === slideIdx ? 'is-active' : ''}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CaptionBlock({ post, plain = false }) {
  const captionLines = (post.caption || '').split('\n');
  if (plain) {
    return (
      <div className="pp-caption pp-caption-plain">
        {captionLines.map((p, i) => (
          <span key={i}>{p}{i < captionLines.length - 1 && <br />}</span>
        ))}
        {post.hashtags?.length > 0 && (
          <div className="pp-caption-tags" style={{ marginTop: 6 }}>{post.hashtags.join(' ')}</div>
        )}
      </div>
    );
  }
  return (
    <div className="pp-caption">
      {post.handle && <span className="pp-caption-handle">{post.handle.replace('@', '')}</span>}{' '}
      {captionLines.map((p, i) => (
        <span key={i}>{p}{i < captionLines.length - 1 && <br />}</span>
      ))}
      {post.hashtags?.length > 0 && (
        <span className="pp-caption-tags"> {post.hashtags.join(' ')}</span>
      )}
    </div>
  );
}

function AuthorAvatar({ char = 'A' }) {
  return (
    <div className="pp-avatar" style={{ background: 'linear-gradient(135deg, #D9CBB3, #A89A82)' }}>
      {String(char || 'A').slice(0, 1).toUpperCase()}
    </div>
  );
}

/* ============================================================
   Per-platform chromes (1 per platform)
   ============================================================ */

function IGChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.handle || '@author';
  return (
    <>
      <div className="pp-app-h">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">{handle}</div>
        <button type="button"><I.dots size={18} /></button>
      </div>
      <div className="pp-author">
        <AuthorAvatar char={handle.replace('@', '')[0]} />
        <div className="pp-author-text">
          <div className="pp-author-name">{handle.replace('@', '')}</div>
          {post.handleSub && <div className="pp-author-sub">{post.handleSub}</div>}
        </div>
        <button className="pp-follow" type="button">Follow</button>
      </div>
      <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} aspect="1 / 1" />
      <div className="pp-ig-actions">
        <span><I.heart size={22} /></span>
        <span><I.chat size={22} /></span>
        <span><I.send size={22} /></span>
        <span style={{ marginLeft: 'auto' }}><I.flag size={22} /></span>
      </div>
      <CaptionBlock post={post} />
    </>
  );
}

function FBChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.handle || 'Account';
  return (
    <>
      <div className="pp-app-h pp-fb">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">{handle}</div>
        <button type="button"><I.search size={18} /></button>
      </div>
      <div className="pp-author">
        <AuthorAvatar char={handle[0]} />
        <div className="pp-author-text">
          <div className="pp-author-name">{handle}</div>
          <div className="pp-author-sub">2h · <I.globe size={9} style={{ display: 'inline', verticalAlign: 'middle' }} /></div>
        </div>
        <button className="pp-iconbtn" type="button"><I.dots size={18} /></button>
      </div>
      <CaptionBlock post={post} plain />
      <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} aspect="1 / 1" />
      {post.metrics && (
        <>
          <div className="pp-fb-counts">
            <span>👍❤️ {fmt(post.metrics.likes)}</span>
            <span>{fmt(post.metrics.comments)} comments · {fmt(post.metrics.shares)} shares</span>
          </div>
        </>
      )}
      <div className="pp-fb-actions">
        <button type="button">👍 Like</button>
        <button type="button">💬 Comment</button>
        <button type="button">↪ Share</button>
      </div>
    </>
  );
}

function LIChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.handle || 'Account';
  return (
    <>
      <div className="pp-app-h">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">Post</div>
        <button type="button"><I.share size={16} /></button>
      </div>
      <div className="pp-author">
        <div className="pp-avatar" style={{ background: 'linear-gradient(135deg, #0A66C2, #0848A0)' }}>{handle[0]}</div>
        <div className="pp-author-text">
          <div className="pp-author-name">{handle}</div>
          {post.handleSub && <div className="pp-author-sub">{post.handleSub}</div>}
        </div>
        <button className="pp-li-follow" type="button">+ Follow</button>
      </div>
      <CaptionBlock post={post} plain />
      <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} aspect="1 / 1" />
      {post.metrics && (
        <div className="pp-li-stats">
          <span>{fmt(post.metrics.likes)} reactions</span>
          <span>·</span>
          <span>{fmt(post.metrics.comments)} comments · {fmt(post.metrics.shares)} reposts</span>
        </div>
      )}
      <div className="pp-li-actions">
        <button type="button">👍 Like</button>
        <button type="button">💬 Comment</button>
        <button type="button">↻ Repost</button>
        <button type="button">↪ Send</button>
      </div>
    </>
  );
}

function XChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.handle || '@account';
  return (
    <>
      <div className="pp-app-h pp-x">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">Post</div>
        <button type="button"><I.dots size={18} /></button>
      </div>
      <div className="pp-x-author">
        <AuthorAvatar char={handle.replace('@', '')[0]} />
        <div className="pp-x-author-text">
          <div className="pp-author-name">{handle.replace('@', '')}</div>
          {post.handleSub && <div className="pp-author-sub">{post.handleSub}</div>}
        </div>
        <button className="pp-x-follow" type="button">Follow</button>
      </div>
      <div className="pp-x-body">
        <CaptionBlock post={post} plain />
        <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} aspect="1 / 1" rounded />
      </div>
      <div className="pp-x-stamp mono">
        {post.publishedAt ? fmtDate(post.publishedAt) : post.scheduledAt ? fmtDate(post.scheduledAt) : ''}
      </div>
      {post.metrics && (
        <div className="pp-x-actions">
          <span><I.chat size={16} /> {fmt(post.metrics.comments)}</span>
          <span><I.refresh size={16} /> {fmt(post.metrics.shares)}</span>
          <span><I.heart size={16} /> {fmt(post.metrics.likes)}</span>
          <span><I.eye size={16} /> {fmt(post.metrics.views)}</span>
          <span style={{ marginLeft: 'auto' }}><I.share size={16} /></span>
        </div>
      )}
    </>
  );
}

function TTChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.handle || '@account';
  const captionFirst = (post.caption || '').split('\n')[0] || post.title;
  return (
    <div className="pp-tt-wrap">
      <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} aspect="9 / 16" full />
      <div className="pp-tt-overlay">
        <div className="pp-tt-top">
          <span className="pp-tt-tab">Following</span>
          <span className="pp-tt-tab is-active">For You</span>
          <span style={{ marginLeft: 'auto' }}><I.search size={20} /></span>
        </div>
        <div className="pp-tt-bottom">
          <div className="pp-tt-text">
            <div className="pp-tt-handle">{handle}</div>
            <div className="pp-tt-caption">{captionFirst}</div>
            {post.hashtags?.length > 0 && (
              <div className="pp-tt-tags">{post.hashtags.join(' ')}</div>
            )}
          </div>
          <div className="pp-tt-side">
            <div className="pp-tt-avatar" style={{ background: 'linear-gradient(135deg, #D9CBB3, #A89A82)' }}>
              {handle.replace('@', '')[0]?.toUpperCase()}
            </div>
            {post.metrics && (
              <>
                <div className="pp-tt-act"><I.heart size={26} /><span>{fmt(post.metrics.likes)}</span></div>
                <div className="pp-tt-act"><I.chat size={26} /><span>{fmt(post.metrics.comments)}</span></div>
                <div className="pp-tt-act"><I.share size={26} /><span>{fmt(post.metrics.shares)}</span></div>
              </>
            )}
            <div className="pp-tt-act"><I.flag size={26} /><span>Save</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TGChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.handle || 'Channel';
  return (
    <>
      <div className="pp-app-h pp-tg">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-tg-h">
          <div className="pp-avatar" style={{ background: 'linear-gradient(135deg, #D9CBB3, #A89A82)', width: 28, height: 28, fontSize: 12 }}>
            {handle[0]}
          </div>
          <div>
            <div className="pp-ig-title">{handle}</div>
            {post.handleSub && <div className="pp-author-sub" style={{ fontSize: 11 }}>{post.handleSub}</div>}
          </div>
        </div>
        <button type="button"><I.dots size={18} /></button>
      </div>
      <div className="pp-tg-msg">
        <div className="pp-tg-bubble">
          <MediaCarousel slides={slides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} aspect="1 / 1" rounded />
          <div style={{ padding: '10px 12px' }}>
            <CaptionBlock post={post} plain />
            {post.metrics && (
              <div className="pp-tg-meta">
                <span>👁 {fmt(post.metrics.views)}</span>
                <span className="mono">{post.publishedAt ? fmtDate(post.publishedAt) : ''}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function ThreadsChrome({ post, slideIdx, setSlideIdx, openLightbox, slides }) {
  const handle = post.handle || '@account';
  // Only render the media tile when we actually have a real image. Threads
  // posts default to text_post in the generation pipeline, but legacy
  // batches and any backend code that seeds a placeholder slide row
  // would otherwise force MediaCarousel to render a black/empty square
  // inside the chrome.
  const visibleSlides = (slides || []).filter((slide) => slide?.imageUrl && !slide.imageIsPlaceholder);
  return (
    <>
      <div className="pp-app-h pp-th">
        <button type="button"><I.chevLeft size={20} /></button>
        <div className="pp-ig-title">Thread</div>
        <button type="button"><I.dots size={18} /></button>
      </div>
      <div className="pp-th-post">
        <div className="pp-th-rail">
          <AuthorAvatar char={handle.replace('@', '')[0]} />
        </div>
        <div className="pp-th-body">
          <div className="pp-th-head">
            <span className="pp-th-handle">{handle.replace('@', '')}</span>
            <span className="pp-author-sub" style={{ marginLeft: 6 }}>2h</span>
            <span style={{ marginLeft: 'auto' }}><I.dots size={14} /></span>
          </div>
          <CaptionBlock post={post} plain />
          {visibleSlides.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <MediaCarousel slides={visibleSlides} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} aspect="1 / 1" rounded />
            </div>
          )}
          <div className="pp-th-actions">
            <span><I.heart size={18} /></span>
            <span><I.chat size={18} /></span>
            <span><I.refresh size={18} /></span>
            <span><I.send size={18} /></span>
          </div>
          {post.metrics && (
            <div className="pp-th-counts">{fmt(post.metrics.likes)} likes · {fmt(post.metrics.comments)} replies</div>
          )}
        </div>
      </div>
    </>
  );
}

/* Phone frame composes the right chrome */
function PhoneFrame({ post, slideIdx, setSlideIdx, openLightbox }) {
  const chromes = {
    instagram: IGChrome,
    facebook: FBChrome,
    linkedin: LIChrome,
    x: XChrome,
    tiktok: TTChrome,
    telegram: TGChrome,
    threads: ThreadsChrome,
  };
  const Chrome = chromes[post.platform] || IGChrome;
  return (
    <div className="pp-phone">
      <div className="pp-phone-shell">
        <div className="pp-phone-notch" />
        <div className={`pp-phone-screen pp-platform-${post.platform}`}>
          <div className="pp-phone-status">
            <span className="mono">9:41</span>
            <span className="pp-phone-status-r">
              <svg width="14" height="9" viewBox="0 0 14 9" fill="currentColor"><circle cx="1.5" cy="7.5" r="1.5" /><circle cx="5.5" cy="6" r="1.5" /><circle cx="9.5" cy="3.5" r="1.5" /></svg>
              <svg width="13" height="9" viewBox="0 0 13 9" fill="currentColor"><path d="M0 4.5L6.5 0L13 4.5L11.5 6L6.5 2L1.5 6L0 4.5Z" /></svg>
              <svg width="18" height="9" viewBox="0 0 18 9" fill="none" stroke="currentColor" strokeWidth="0.8"><rect x="0.4" y="0.4" width="14" height="8.2" rx="1.5" /><rect x="2" y="2" width="11" height="5" rx="0.5" fill="currentColor" /><rect x="15.2" y="3" width="1.4" height="3" rx="0.5" fill="currentColor" /></svg>
            </span>
          </div>
          <Chrome post={post} slideIdx={slideIdx} setSlideIdx={setSlideIdx} openLightbox={openLightbox} slides={post.slides} />
          <div className="pp-phone-home" />
        </div>
      </div>
    </div>
  );
}

/* Lightbox with Esc + arrow nav */
function Lightbox({ post, slideIdx, setSlideIdx, onClose }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setSlideIdx((i) => Math.min(post.slides.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setSlideIdx((i) => Math.max(0, i - 1));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [post, onClose, setSlideIdx]);

  const slide = post.slides[slideIdx];
  const imgStyle = slide.imageUrl
    ? { backgroundImage: `url("${slide.imageUrl}")` }
    : { background: `linear-gradient(135deg, ${slide.grad[0]}, ${slide.grad[1]})` };

  return (
    <div className="pp-lightbox" onClick={onClose}>
      <button className="pp-lb-close" onClick={onClose} type="button"><I.x size={18} /></button>
      <div className="pp-lb-img" onClick={(e) => e.stopPropagation()} style={imgStyle}>
        {slide.cap && (
          <div className="pp-lb-cap" style={slide.serif ? { fontFamily: 'var(--font-serif)', fontStyle: 'italic' } : undefined}>
            {slide.cap.split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
        )}
        {slide.tag && <span className="pp-lb-tag mono">{slide.tag}</span>}
      </div>
      {post.slides.length > 1 && (
        <div className="pp-lb-bar" onClick={(e) => e.stopPropagation()}>
          <button
            className="pp-lb-btn" disabled={slideIdx === 0}
            onClick={() => setSlideIdx((i) => Math.max(0, i - 1))} type="button"
          >
            <I.chevLeft size={14} /> Prev
          </button>
          <div className="pp-lb-dots">
            {post.slides.map((_, i) => (
              <button
                key={i} className={`pp-lb-dot ${i === slideIdx ? 'is-active' : ''}`}
                onClick={() => setSlideIdx(i)} type="button" aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <button
            className="pp-lb-btn" disabled={slideIdx === post.slides.length - 1}
            onClick={() => setSlideIdx((i) => Math.min(post.slides.length - 1, i + 1))} type="button"
          >
            Next <I.chevRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Threads-specific panels: live insights + replies inbox.
   Visible only when the post lives on Threads and has an external
   post id (= it's published).
   ============================================================ */
function ThreadsInsightsSection({ externalPostId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!externalPostId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.threads.getInsights(externalPostId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load insights'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [externalPostId, refreshKey]);

  if (!externalPostId) return null;

  return (
    <div className="pp-section">
      <div className="eyebrow"><span>Threads insights</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 className="pp-section-h" style={{ margin: 0 }}>Live performance</h3>
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
        >
          <I.refresh size={12} /> Refresh
        </button>
      </div>
      {loading && <div className="muted" style={{ fontSize: 13 }}>Loading insights…</div>}
      {error && (
        <div className="auth-error" style={{ marginTop: 8 }}>
          <I.alert size={14} /> <span>{error}</span>
        </div>
      )}
      {data && !loading && (
        <div className="pp-metrics">
          <div className="pp-metric"><I.eye size={13} /><span className="pp-m-v mono">{fmt(data.views)}</span><span className="pp-m-l">Views</span></div>
          <div className="pp-metric"><I.heart size={13} /><span className="pp-m-v mono">{fmt(data.likes)}</span><span className="pp-m-l">Likes</span></div>
          <div className="pp-metric"><I.chat size={13} /><span className="pp-m-v mono">{fmt(data.replies)}</span><span className="pp-m-l">Replies</span></div>
          <div className="pp-metric"><I.refresh size={13} /><span className="pp-m-v mono">{fmt(data.reposts)}</span><span className="pp-m-l">Reposts</span></div>
          <div className="pp-metric"><I.share size={13} /><span className="pp-m-v mono">{fmt(data.quotes)}</span><span className="pp-m-l">Quotes</span></div>
        </div>
      )}
    </div>
  );
}

function ThreadsRepliesSection({ externalPostId, postOwnerHandle }) {
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [hidingId, setHidingId] = useState(null);

  useEffect(() => {
    if (!externalPostId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.threads.listReplies(externalPostId)
      .then((res) => { if (!cancelled) setReplies(res?.replies || []); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load replies'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [externalPostId, refreshKey]);

  if (!externalPostId) return null;

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    // Optimistic insert — show the reply right away with a "Sending…"
    // badge so the UI feels instant. The real reply replaces it once
    // the server confirms (refreshKey re-fetches the canonical list).
    const optimisticId = `__optimistic_${Date.now()}`;
    setReplies((prev) => [
      { id: optimisticId, text, username: prev[0]?.username || 'you', timestamp: new Date().toISOString(), hidden: false, permalink: null, pending: true },
      ...prev,
    ]);
    setDraft('');
    try {
      await api.threads.sendReply(externalPostId, text);
      // Refresh — server-side reply will replace the optimistic row.
      setRefreshKey((k) => k + 1);
    } catch (err) {
      // Roll back the optimistic row and surface the failure inline.
      setReplies((prev) => prev.filter((r) => r.id !== optimisticId));
      setDraft(text);
      setError(err?.message || 'Reply failed');
    } finally {
      setSending(false);
    }
  };

  const toggleHide = async (reply) => {
    setHidingId(reply.id);
    try {
      if (reply.hidden) {
        await api.threads.unhideReply(reply.id);
      } else {
        await api.threads.hideReply(reply.id);
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      window.alert(`Update failed: ${err.message || 'Unknown error'}`);
    } finally {
      setHidingId(null);
    }
  };

  return (
    <div className="pp-section">
      <div className="eyebrow"><span>Threads replies</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 className="pp-section-h" style={{ margin: 0 }}>Conversation</h3>
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
        >
          <I.refresh size={12} /> Refresh
        </button>
      </div>

      {/* Reply composer — textarea above, action row below for stable
          alignment regardless of how tall the user grows the textarea. */}
      <div
        style={{
          marginBottom: 14,
          padding: 10,
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-elev)',
          transition: 'border-color var(--dur-fast), box-shadow var(--dur-fast)',
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Reply as the post author…"
          rows={2}
          disabled={sending}
          style={{
            width: '100%',
            border: 0,
            outline: 0,
            background: 'transparent',
            color: 'var(--text)',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            lineHeight: 1.5,
            resize: 'vertical',
            minHeight: 44,
            padding: 0,
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border-subtle)',
        }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
            {/* "Posts as" must show the CONNECTED ACCOUNT (post owner), not
                the latest commenter's handle. Reading replies[0]?.username
                was a bug — the first reply could be from anyone, including
                the user who just commented from another account, which
                made the reply box claim it would post as THEM. Use the
                postOwnerHandle prop (passed from the page's post.handle). */}
            {draft.length > 0
              ? `${draft.length} / 500`
              : `Posts as ${postOwnerHandle ? (postOwnerHandle.startsWith('@') ? postOwnerHandle : '@' + postOwnerHandle) : '@you'}`}
          </span>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            style={{ minWidth: 84, justifyContent: 'center' }}
          >
            {sending ? (
              <>
                <span className="dot pulse" style={{ background: 'currentColor', width: 6, height: 6 }} />
                Sending
              </>
            ) : (
              <><I.send size={12} /> Send</>
            )}
          </button>
        </div>
      </div>

      {loading && <div className="muted" style={{ fontSize: 13 }}>Loading replies…</div>}
      {error && (
        <div className="auth-error">
          <I.alert size={14} /> <span>{error}</span>
        </div>
      )}
      {!loading && !error && replies.length === 0 && (
        <div className="muted" style={{ fontSize: 13 }}>No replies yet.</div>
      )}
      {!loading && replies.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {replies.map((r) => (
            <li
              key={r.id}
              style={{
                padding: 10,
                background: r.hidden ? 'var(--bg-subtle)' : 'var(--bg-elev)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--r-md)',
                opacity: r.pending ? 0.7 : r.hidden ? 0.55 : 1,
                transition: 'opacity var(--dur-fast), background var(--dur-fast)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>@{r.username || 'user'}</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {r.pending && <span className="dot pulse" style={{ background: 'var(--accent)', width: 6, height: 6 }} />}
                  {r.pending ? 'Sending…' : (r.timestamp ? fmtDate(r.timestamp) : '')}
                </span>
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.4, color: 'var(--text)' }}>
                {r.text || <em style={{ color: 'var(--text-subtle)' }}>(no text)</em>}
              </div>
              {!r.pending && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  {r.permalink && (
                    <a
                      className="btn btn-ghost btn-sm"
                      href={r.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <I.external size={11} /> View
                    </a>
                  )}
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => toggleHide(r)}
                    disabled={hidingId === r.id}
                  >
                    {r.hidden ? <><I.eye size={11} /> Unhide</> : <><I.alert size={11} /> Hide</>}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ============================================================
   Instagram-specific panels: insights + comments.
   Mirrors the Threads pair but talks to api.instagram.* and uses
   Meta Graph IG Business endpoint metrics (impressions, reach, …).
   Visible only when the post is on Instagram and has an external
   media ID (= it's published).
   ============================================================ */
function InstagramInsightsSection({ externalPostId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!externalPostId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.instagram.getInsights(externalPostId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load insights'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [externalPostId, refreshKey]);

  if (!externalPostId) return null;

  // Worker returns { metrics: { impressions, reach, likes, comments, saves, shares } }
  const metrics = data?.metrics || {};

  return (
    <div className="pp-section">
      <div className="eyebrow"><span>Instagram insights</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 className="pp-section-h" style={{ margin: 0 }}>Live performance</h3>
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
        >
          <I.refresh size={12} /> Refresh
        </button>
      </div>
      {loading && <div className="muted" style={{ fontSize: 13 }}>Loading insights…</div>}
      {error && (
        <div className="auth-error" style={{ marginTop: 8 }}>
          <I.alert size={14} /> <span>{error}</span>
        </div>
      )}
      {data && !loading && (
        <div className="pp-metrics">
          <div className="pp-metric"><I.eye size={13} /><span className="pp-m-v mono">{fmt(metrics.reach)}</span><span className="pp-m-l">Reach</span></div>
          <div className="pp-metric"><I.heart size={13} /><span className="pp-m-v mono">{fmt(metrics.likes)}</span><span className="pp-m-l">Likes</span></div>
          <div className="pp-metric"><I.chat size={13} /><span className="pp-m-v mono">{fmt(metrics.comments)}</span><span className="pp-m-l">Comments</span></div>
          <div className="pp-metric"><I.bookmark size={13} /><span className="pp-m-v mono">{fmt(metrics.saved)}</span><span className="pp-m-l">Saved</span></div>
          <div className="pp-metric"><I.share size={13} /><span className="pp-m-v mono">{fmt(metrics.shares)}</span><span className="pp-m-l">Shares</span></div>
          <div className="pp-metric"><I.sparkles size={13} /><span className="pp-m-v mono">{fmt(metrics.total_interactions)}</span><span className="pp-m-l">Interactions</span></div>
        </div>
      )}
    </div>
  );
}

function InstagramCommentsSection({ externalPostId, postOwnerHandle }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [hidingId, setHidingId] = useState(null);

  useEffect(() => {
    if (!externalPostId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.instagram.listComments(externalPostId)
      .then((res) => { if (!cancelled) setComments(res?.comments || []); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load comments'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [externalPostId, refreshKey]);

  if (!externalPostId) return null;

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    const optimisticId = `__optimistic_${Date.now()}`;
    setComments((prev) => [
      { id: optimisticId, text, username: postOwnerHandle ? postOwnerHandle.replace(/^@/, '') : 'you', timestamp: new Date().toISOString(), hidden: false, pending: true },
      ...prev,
    ]);
    setDraft('');
    try {
      await api.instagram.sendComment(externalPostId, text);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setComments((prev) => prev.filter((c) => c.id !== optimisticId));
      setDraft(text);
      setError(err?.message || 'Comment failed');
    } finally {
      setSending(false);
    }
  };

  const toggleHide = async (comment) => {
    setHidingId(comment.id);
    try {
      if (comment.hidden) {
        await api.instagram.unhideComment(comment.id);
      } else {
        await api.instagram.hideComment(comment.id);
      }
      setRefreshKey((k) => k + 1);
    } catch (err) {
      window.alert(`Update failed: ${err.message || 'Unknown error'}`);
    } finally {
      setHidingId(null);
    }
  };

  return (
    <div className="pp-section">
      <div className="eyebrow"><span>Instagram comments</span></div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
        <h3 className="pp-section-h" style={{ margin: 0 }}>Conversation</h3>
        <button
          className="btn btn-ghost btn-sm"
          type="button"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
        >
          <I.refresh size={12} /> Refresh
        </button>
      </div>

      <div
        style={{
          marginBottom: 14,
          padding: 10,
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          background: 'var(--bg-elev)',
          transition: 'border-color var(--dur-fast), box-shadow var(--dur-fast)',
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Comment as the post author…"
          rows={2}
          disabled={sending}
          style={{
            width: '100%',
            border: 0,
            outline: 0,
            background: 'transparent',
            color: 'var(--text)',
            fontFamily: 'var(--font-sans)',
            fontSize: 14,
            lineHeight: 1.5,
            resize: 'vertical',
            minHeight: 44,
            padding: 0,
          }}
        />
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border-subtle)',
        }}>
          <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
            {draft.length > 0
              ? `${draft.length} / 2200`
              : `Posts as ${postOwnerHandle ? (postOwnerHandle.startsWith('@') ? postOwnerHandle : '@' + postOwnerHandle) : '@you'}`}
          </span>
          <button
            className="btn btn-primary btn-sm"
            type="button"
            onClick={handleSend}
            disabled={!draft.trim() || sending}
            style={{ minWidth: 84, justifyContent: 'center' }}
          >
            {sending ? (
              <>
                <span className="dot pulse" style={{ background: 'currentColor', width: 6, height: 6 }} />
                Sending
              </>
            ) : (
              <><I.send size={12} /> Send</>
            )}
          </button>
        </div>
      </div>

      {loading && <div className="muted" style={{ fontSize: 13 }}>Loading comments…</div>}
      {error && (
        <div className="auth-error">
          <I.alert size={14} /> <span>{error}</span>
        </div>
      )}
      {!loading && !error && comments.length === 0 && (
        <div className="muted" style={{ fontSize: 13 }}>No comments yet.</div>
      )}
      {!loading && comments.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {comments.map((c) => (
            <li
              key={c.id}
              style={{
                padding: 10,
                background: c.hidden ? 'var(--bg-subtle)' : 'var(--bg-elev)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--r-md)',
                opacity: c.pending ? 0.7 : c.hidden ? 0.55 : 1,
                transition: 'opacity var(--dur-fast), background var(--dur-fast)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4, gap: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 13 }}>@{c.username || 'user'}</span>
                <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {c.pending && <span className="dot pulse" style={{ background: 'var(--accent)', width: 6, height: 6 }} />}
                  {c.pending ? 'Sending…' : (c.timestamp ? fmtDate(c.timestamp) : '')}
                </span>
              </div>
              <div style={{ fontSize: 13.5, lineHeight: 1.4, color: 'var(--text)' }}>
                {c.text || <em style={{ color: 'var(--text-subtle)' }}>(no text)</em>}
              </div>
              {!c.pending && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => toggleHide(c)}
                    disabled={hidingId === c.id}
                  >
                    {c.hidden ? <><I.eye size={11} /> Unhide</> : <><I.alert size={11} /> Hide</>}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* Details panel — status-conditional actions + metrics */
function DetailsPanel({ post, onPublishNow, onRepublish, onSchedule, onEdit, onRegenerate, onDelete, onReschedule }) {
  const isPublished = post.status === 'published';
  const isScheduled = post.status === 'scheduled' || post.status === 'planned';
  const isDraft = post.status === 'draft' || post.status === 'ready';

  const statusBadge = isPublished
    ? <span className="pill pill-success"><span className="dot" />Published</span>
    : isScheduled
      ? <span className="pill pill-info"><span className="dot" />Scheduled</span>
      : <span className="pill"><span className="dot" />Draft</span>;

  return (
    <aside className="pp-details">
      <div className="pp-d-h">
        <div className="pp-d-h-l">
          {statusBadge}
          <h2 className="pp-d-h-title">{post.title}</h2>
          <div className="pp-d-h-sub">
            <span
              className={`ob-net-ic-${post.platform}`}
              style={{ width: 18, height: 18, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
            >
              {BrandIcons[post.platform]?.({ size: 11 })}
            </span>
            <span>{post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}</span>
            {post.handle && (
              <>
                <span className="pp-d-sep">·</span>
                <span>{post.handle}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="pp-actions">
        {isScheduled && (
          <>
            <div className="pp-action-meta">
              <span className="pp-action-l">Scheduled for</span>
              <span className="pp-action-v"><I.clock size={13} /> {fmtDate(post.scheduledAt) || 'TBD'}</span>
            </div>
            <button className="btn btn-primary btn-lg pp-action-cta" onClick={onPublishNow} type="button">
              <I.zap size={14} /> Publish now
            </button>
            <div className="pp-action-row">
              <button className="btn btn-ghost btn-sm" onClick={onEdit} type="button">
                <I.edit size={12} /> Edit
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onReschedule} type="button">
                <I.calendar size={12} /> Reschedule
              </button>
              <button className="btn btn-ghost btn-sm pp-danger" onClick={onDelete} type="button">
                <I.trash size={12} /> Delete
              </button>
            </div>
          </>
        )}

        {isPublished && (
          <>
            <div className="pp-action-meta">
              <span className="pp-action-l">Published</span>
              <span className="pp-action-v"><I.clock size={13} /> {fmtDate(post.publishedAt) || 'recently'}</span>
            </div>
            <button className="btn btn-primary btn-lg pp-action-cta" onClick={onRepublish} type="button">
              <I.refresh size={14} /> Publish again
            </button>
            <div className="pp-action-row">
              {post.permalink && (
                <a
                  className="btn btn-ghost btn-sm"
                  href={post.permalink.startsWith('http') ? post.permalink : `https://${post.permalink}`}
                  target="_blank" rel="noopener noreferrer"
                >
                  <I.external size={12} /> View on {post.platform}
                </a>
              )}
              {/* Delete for published posts is platform-specific: only
                  Threads supports propagating delete to the platform via
                  Graph API. Other networks would only soft-delete locally,
                  which is misleading — hide the button there. */}
              {post.platform === 'threads' && (
                <button className="btn btn-ghost btn-sm pp-danger" onClick={onDelete} type="button">
                  <I.trash size={12} /> Delete
                </button>
              )}
            </div>
          </>
        )}

        {isDraft && (
          <>
            <div className="pp-action-meta">
              <span className="pp-action-l">Draft</span>
              <span className="pp-action-v"><I.clock size={13} /> saved {fmtDate(post.createdAt) || ''}</span>
            </div>
            <button className="btn btn-primary btn-lg pp-action-cta" onClick={onSchedule} type="button">
              <I.calendar size={14} /> Schedule this post
            </button>
            <div className="pp-action-row">
              <button className="btn btn-ghost btn-sm" onClick={onEdit} type="button">
                <I.edit size={12} /> Edit
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onRegenerate} type="button">
                <I.sparkles size={12} /> Regenerate
              </button>
              <button className="btn btn-ghost btn-sm pp-danger" onClick={onDelete} type="button">
                <I.trash size={12} /> Delete
              </button>
            </div>
          </>
        )}
      </div>

      {/* Threads-only sections — visible after the post is published to
          Threads. Pull live data from Graph API via threads-worker. */}
      {isPublished && post.platform === 'threads' && post.externalPostId && (
        <>
          <ThreadsInsightsSection externalPostId={post.externalPostId} />
          <ThreadsRepliesSection externalPostId={post.externalPostId} postOwnerHandle={post.handle} />
        </>
      )}

      {/* Instagram-only sections — visible after the post is published to
          Instagram. Pull live data from Meta Graph IG Business API via
          instagram-worker. */}
      {isPublished && post.platform === 'instagram' && post.externalPostId && (
        <>
          <InstagramInsightsSection externalPostId={post.externalPostId} />
          <InstagramCommentsSection externalPostId={post.externalPostId} postOwnerHandle={post.handle} />
        </>
      )}

      {isPublished && post.metrics && (
        <div className="pp-section">
          <div className="eyebrow"><span>Performance</span></div>
          <h3 className="pp-section-h">How it's doing</h3>
          <div className="pp-metrics">
            <div className="pp-metric"><I.eye size={13} /><span className="pp-m-v mono">{fmt(post.metrics.views)}</span><span className="pp-m-l">Views</span></div>
            <div className="pp-metric"><I.heart size={13} /><span className="pp-m-v mono">{fmt(post.metrics.likes)}</span><span className="pp-m-l">Likes</span></div>
            <div className="pp-metric"><I.chat size={13} /><span className="pp-m-v mono">{fmt(post.metrics.comments)}</span><span className="pp-m-l">Comments</span></div>
            <div className="pp-metric"><I.share size={13} /><span className="pp-m-v mono">{fmt(post.metrics.shares)}</span><span className="pp-m-l">Shares</span></div>
            <div className="pp-metric"><I.layers size={13} /><span className="pp-m-v mono">{fmt(post.metrics.saves)}</span><span className="pp-m-l">Saves</span></div>
            {post.metrics.reach > 0 && (
              <div className="pp-metric"><I.users size={13} /><span className="pp-m-v mono">{fmt(post.metrics.reach)}</span><span className="pp-m-l">Reach</span></div>
            )}
          </div>
          {typeof post.metrics.engagement === 'number' && (
            <div className="pp-eng">
              <I.trend size={14} />
              <span>Engagement rate</span>
              <span className="pp-eng-v">{post.metrics.engagement.toFixed(1)}%</span>
            </div>
          )}
        </div>
      )}

      <div className="pp-section">
        <div className="eyebrow"><span>Details</span></div>
        <ul className="pp-detail-list">
          <li>
            <span className="pp-detail-l">Platform</span>
            <span className="pp-detail-v">
              <span
                className={`ob-net-ic-${post.platform}`}
                style={{ width: 18, height: 18, borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
              >
                {BrandIcons[post.platform]?.({ size: 11 })}
              </span>
              {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
            </span>
          </li>
          <li>
            <span className="pp-detail-l">Media</span>
            <span className="pp-detail-v">
              {post.format === 'text_post' || post.slides.length === 0
                ? 'Text only'
                : post.slides.length === 1
                ? 'Single image'
                : `Carousel · ${post.slides.length} slides`}
            </span>
          </li>
          {post.altText && (
            <li className="pp-detail-stack">
              <span className="pp-detail-l">Alt text</span>
              <span className="pp-detail-v pp-detail-v-text">{post.altText}</span>
            </li>
          )}
          {post.createdAt && (
            <li>
              <span className="pp-detail-l">Created</span>
              <span className="pp-detail-v">{fmtDate(post.createdAt)}</span>
            </li>
          )}
        </ul>
      </div>
    </aside>
  );
}

/* ============================================================
   Main screen
   ============================================================ */

export const PostPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetHint = useMemo(() => ({
    targetId: searchParams.get('target') || null,
    platform: searchParams.get('platform') || null,
  }), [searchParams]);
  const { setActivity } = useActivity();

  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slideIdx, setSlideIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.content.get(id)
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setRaw(payload);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Could not load post');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  // While the job is still generating, the active version + slides don't
  // exist yet — the phone chrome would render empty. Poll the content
  // endpoint every 3s and swap to the real post as soon as the worker
  // finishes. After ready, keep polling for ~30s so the publish_target
  // status transition (scheduled → published) also lands without forcing
  // the user to refresh.
  const generationStatus = raw?.contentItem?.generationStatus || raw?.generationStatus || null;
  // Visual can still be cooking via ai-generation-queue even after text
  // generation flips the content_item's generation_status to 'ready'. In
  // that window the user used to see a fully-rendered preview with a
  // placeholder image — surface visual_status from the job snapshot so
  // we keep the generating overlay up until the image actually lands
  // (or fails). Defensive: only apply for image_post items, otherwise
  // text-only formats would never finalize.
  const jobFormat = raw?.job?.format || raw?.contentItem?.format;
  const jobVisualStatus = (raw?.job?.visualStatus || '').toLowerCase();
  const visualStillCooking = jobFormat === 'image_post'
    && !['succeeded', 'failed'].includes(jobVisualStatus)
    && !(raw?.activeVersion?.imageUrl)
    && !(Array.isArray(raw?.activeVersion?.mediaUrls) && raw.activeVersion.mediaUrls.length > 0);
  const isGenerating = visualStillCooking
    || (generationStatus && !['ready', 'failed'].includes(generationStatus));
  const isAwaitingPublish = !isGenerating
    && raw?.publishSummary
    && !raw.publishSummary.lastPublishedAt
    && Array.isArray(raw.publishSummary.targets)
    && raw.publishSummary.targets.some((t) => t?.status === 'queued' || t?.status === 'scheduled' || t?.priority === 'now');
  const shouldPoll = isGenerating || isAwaitingPublish;
  useEffect(() => {
    if (!id || !shouldPoll) return;
    let cancelled = false;
    let stopAt = isGenerating ? Infinity : Date.now() + 30_000;
    const tick = () => {
      if (cancelled) return;
      if (Date.now() > stopAt) { clearInterval(interval); return; }
      api.content.get(id)
        .then((res) => {
          if (cancelled) return;
          const payload = res?.data && typeof res.data === 'object' ? res.data : res;
          setRaw(payload);
        })
        .catch(() => { /* swallow; next tick may succeed */ });
    };
    const interval = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [id, shouldPoll, isGenerating]);

  const post = useMemo(() => mapServerPost(raw, targetHint), [raw, targetHint]);

  useEffect(() => { setSlideIdx(0); setLightbox(false); }, [id]);

  if (loading) {
    return (
      <div className="pp-root">
        <div className="pp-top">
          <div>
            <Link to="/app/library" className="pp-back"><I.chevLeft size={14} /> Library</Link>
            <h1 className="pp-h">Loading…</h1>
          </div>
        </div>
      </div>
    );
  }
  if (error || !post) {
    return (
      <div className="pp-root">
        <div className="pp-top">
          <div>
            <Link to="/app/library" className="pp-back"><I.chevLeft size={14} /> Library</Link>
            <h1 className="pp-h">Post not found</h1>
            <p className="pp-sub">{error || 'This post does not exist or you do not have access to it.'}</p>
          </div>
        </div>
      </div>
    );
  }

  // Job is still running — render a phone chrome at the same dimensions as
  // the real post preview, but with shimmer placeholders inside. The phone
  // sits in a .pp-phone wrapper (the same one the real preview uses) so the
  // loading state literally morphs into the post when generation finishes.
  if (isGenerating) {
    const batchId = raw?.contentItem?.generationBatchId || raw?.generationBatchId || raw?.batch?.id || null;
    const stage = generationStatus === 'visualizing' ? 'Generating image'
      : generationStatus === 'drafting' ? 'Writing draft'
      : 'Working on it';
    return (
      <div className="pp-root pp-root-generating">
        <div className="pp-top">
          <div>
            <Link to="/app/library" className="pp-back"><I.chevLeft size={14} /> Library</Link>
          </div>
        </div>
        <div className="pp-generating-stage" role="status" aria-live="polite">
          <div className="pp-phone">
            <div className="pp-phone-shell">
              <div className="pp-phone-notch" />
              <div className="pp-phone-screen pp-phone-screen-loading">
                <div className="pp-phone-status">
                  <span>9:41</span>
                  <span className="pp-phone-status-r">•••</span>
                </div>
                <div className="pp-loading-header" aria-hidden="true">
                  <div className="pp-loading-avatar" />
                  <div className="pp-loading-handle">
                    <div className="pp-loading-pill pp-loading-pill-w50" />
                    <div className="pp-loading-pill pp-loading-pill-w30" />
                  </div>
                </div>
                <div className="pp-loading-media" aria-hidden="true">
                  <div className="pp-generating-spinner" />
                </div>
                <div className="pp-loading-body" aria-hidden="true">
                  <div className="pp-loading-pill pp-loading-pill-w90" />
                  <div className="pp-loading-pill pp-loading-pill-w70" />
                  <div className="pp-loading-pill pp-loading-pill-w50" />
                </div>
                <div className="pp-phone-home" />
              </div>
            </div>
          </div>
          <div className="pp-generating-caption">
            <div className="pp-generating-status" aria-hidden="true">
              <span className="pp-generating-pulse-dot" />
              <span className="pp-generating-eyebrow">Generating</span>
            </div>
            <h2 className="pp-generating-h">
              {stage}<span className="pp-generating-dots" aria-hidden="true">…</span>
            </h2>
            <p className="pp-generating-sub">
              Your preview will appear here when ready. Feel free to leave — we keep generating in the background.
            </p>
            {batchId && (
              <button
                type="button"
                className="pp-generating-cta"
                onClick={() => navigate(`/app/batch/${encodeURIComponent(batchId)}`)}
              >
                <I.chevLeft size={14} style={{ transform: 'rotate(180deg)' }} />
                View batch progress
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Extract a useful error message from API errors (which wrap the original).
  const errMessage = (err) => {
    if (!err) return 'Unknown error';
    return err?.body?.error?.message
      || err?.body?.message
      || err?.data?.message
      || err?.message
      || 'Unknown error';
  };

  // "Publish now" — for scheduled posts. Reschedule to "now" so the dispatcher
  // sweep picks it up immediately. Falls back to retry() for failed targets.
  const handlePublishNow = async () => {
    if (!post.scheduledPostId) {
      window.alert('No scheduled post on this content yet. Schedule it first from the calendar.');
      return;
    }
    setActivity({ kind: 'planning', step: `Publishing to ${post.platform}…`, project: post.title });
    const nowIso = new Date().toISOString();
    try {
      await api.scheduler.reschedule(post.scheduledPostId, { scheduledAt: nowIso });
      setActivity({ kind: 'done', message: `Queued for ${post.platform}` });
    } catch (err) {
      // If failed targets exist, retry them instead
      try {
        await api.scheduler.retry(post.scheduledPostId);
        setActivity({ kind: 'done', message: `Retry queued for ${post.platform}` });
      } catch (err2) {
        setActivity(null);
        window.alert(`Publish failed: ${errMessage(err2) || errMessage(err)}`);
      }
    }
  };

  // "Publish again" — clone the published scheduled_post into a new one targeting
  // the same platform, scheduled for now.
  const handleRepublish = async () => {
    if (!post.scheduledPostId) {
      window.alert('No scheduled post on this content. Cannot republish.');
      return;
    }
    setActivity({ kind: 'planning', step: `Re-publishing to ${post.platform}…`, project: post.title });
    const nowIso = new Date().toISOString();
    try {
      await api.scheduler.clone(post.scheduledPostId, {
        targetPlatforms: [post.platform],
        scheduledAt: nowIso,
        scheduleMode: 'publish_now',
      });
      setActivity({ kind: 'done', message: `Re-publish queued for ${post.platform}` });
    } catch (err) {
      setActivity(null);
      window.alert(`Re-publish failed: ${errMessage(err)}`);
    }
  };
  // Both "Schedule this post" (on a draft) and "Reschedule" (on a scheduled
  // post) deep-link into EditPost's schedule tab — that's where the actual
  // scheduling form + Save logic live. The previous /app/calendar?content=…
  // route had no handler for the query string, so the click went nowhere.
  // Forward the platform hint (and target hint) so EditPost can scope the
  // chrome + per-account picker to the same platform the user is viewing.
  // Without this, a Telegram preview opening Edit would surface ALL accounts
  // from the batch (including IG/Threads peers), which is the wrong scope.
  const editQs = (() => {
    const qs = new URLSearchParams();
    if (targetHint.platform) qs.set('platform', targetHint.platform);
    if (targetHint.targetId) qs.set('target', targetHint.targetId);
    return qs.toString();
  })();
  const editSuffix = editQs ? `&${editQs}` : '';
  const handleSchedule = () => navigate(`/app/post/${encodeURIComponent(post.id)}/edit?tab=schedule${editSuffix}`);
  const handleEdit = () => navigate(`/app/post/${encodeURIComponent(post.id)}/edit${editQs ? `?${editQs}` : ''}`);
  const handleReschedule = () => navigate(`/app/post/${encodeURIComponent(post.id)}/edit?tab=schedule${editSuffix}`);
  const handleDelete = async () => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      // For published Threads posts, propagate deletion to the platform
      // first so the original disappears from Threads. Best-effort — if it
      // fails (post already gone, permission denied), we still archive
      // locally so the user can move on. Non-Threads platforms or drafts
      // skip this step entirely.
      if (post.platform === 'threads' && post.externalPostId && post.status === 'published') {
        try {
          await api.threads.deletePost(post.externalPostId);
        } catch (err) {
          console.warn('Threads delete failed, archiving locally only', err);
        }
      }
      await api.content.archive(post.id);
      navigate('/app/library');
    } catch (err) {
      window.alert(err?.message || 'Could not delete');
    }
  };
  const handleRegenerate = async () => {
    try {
      setActivity({ kind: 'planning', step: 'Regenerating…', project: post.title });
      await api.content.regenerate(post.id);
      setActivity({ kind: 'done', message: 'Regeneration started' });
    } catch (err) {
      setActivity(null);
      window.alert(err?.message || 'Could not regenerate');
    }
  };

  return (
    <div className="pp-root">
      <div className="pp-top">
        <div>
          <Link to="/app/library" className="pp-back"><I.chevLeft size={14} /> Library</Link>
          <h1 className="pp-h">Post preview</h1>
          <p className="pp-sub">
            Read the post the way your followers will read it. Tap the cover for full-screen.
          </p>
        </div>
      </div>

      <div className="pp-grid">
        <PhoneFrame
          post={post}
          slideIdx={slideIdx}
          setSlideIdx={setSlideIdx}
          openLightbox={() => setLightbox(true)}
        />
        <DetailsPanel
          post={post}
          onPublishNow={handlePublishNow}
          onRepublish={handleRepublish}
          onSchedule={handleSchedule}
          onEdit={handleEdit}
          onReschedule={handleReschedule}
          onDelete={handleDelete}
          onRegenerate={handleRegenerate}
        />
      </div>

      {lightbox && (
        <Lightbox
          post={post}
          slideIdx={slideIdx}
          setSlideIdx={setSlideIdx}
          onClose={() => setLightbox(false)}
        />
      )}
    </div>
  );
};

export default PostPreview;
